import React, { useState } from 'react';
import { Card, Tag, Empty, Space, Statistic } from 'antd';
import { CalendarOutlined, TagOutlined, AppstoreOutlined, BookOutlined } from '@ant-design/icons';
import ArchiveItem from '../../components/ArchiveItem';
import { posts } from '../../api/mockData';
import './index.css';

const Archive = () => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);

  // 按年份分组
  const postsByYear = posts.reduce((acc, post) => {
    const year = post.date.split('-')[0];
    if (!acc[year]) acc[year] = [];
    acc[year].push(post);
    return acc;
  }, {});

  // 按月份统计
  const postsByMonth = posts.reduce((acc, post) => {
    const date = new Date(post.date);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const key = `${year}-${month}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(post);
    return acc;
  }, {});

  // 获取所有标签
  const allTags = Array.from(new Set(posts.flatMap(post => post.tags)));

  // 获取所有年份
  const years = Object.keys(postsByYear).sort((a, b) => b - a);

  // 获取年度统计数据
  const getYearStats = (year) => {
    const yearPosts = postsByYear[year] || [];
    const monthCount = new Set(yearPosts.map(p => new Date(p.date).getMonth() + 1)).size;
    const tagCount = new Set(yearPosts.flatMap(p => p.tags)).size;
    
    return {
      postCount: yearPosts.length,
      monthCount,
      tagCount,
      firstPost: yearPosts[0],
      lastPost: yearPosts[yearPosts.length - 1]
    };
  };

  // 获取筛选后的文章
  const getFilteredPosts = () => {
    if (selectedYear && selectedTag) {
      return posts.filter(post => 
        post.date.startsWith(selectedYear) && 
        post.tags.includes(selectedTag)
      );
    }
    if (selectedYear) {
      return postsByYear[selectedYear] || [];
    }
    if (selectedTag) {
      return posts.filter(post => post.tags.includes(selectedTag));
    }
    return posts;
  };

  // 清除筛选
  const clearFilters = () => {
    setSelectedYear(null);
    setSelectedTag(null);
  };

  return (
    <div className="archive-page">
      <div className="page-header">
        <h2 className="page-title">
          <span className="chinese-seal-small">档</span>
          文物归档
        </h2>
        <p className="page-subtitle">按时间脉络，梳理文明记忆</p>
      </div>

      <div className="archive-content">
        {/* 侧边栏 - 筛选和统计 */}
        <div className="archive-sidebar">
          <Card 
            className="filter-card"
            styles={{
              body: { padding: '20px' }
            }}
          >
            <h3 className="card-title">
              <AppstoreOutlined /> 归档筛选
            </h3>
            
            <div className="filter-section">
              <h4>按年份筛选</h4>
              <Space wrap className="year-tags">
                {years.map(year => (
                  <Tag
                    key={year}
                    color={selectedYear === year ? 'default' : 'blue'}
                    className={`year-tag ${selectedYear === year ? 'selected' : ''}`}
                    onClick={() => setSelectedYear(year === selectedYear ? null : year)}
                  >
                    {year}年
                  </Tag>
                ))}
              </Space>
            </div>

            <div className="filter-section">
              <h4>按标签筛选</h4>
              <Space wrap className="category-tags">
                {allTags.map(tag => (
                  <Tag
                    key={tag}
                    color={selectedTag === tag ? 'default' : 'green'}
                    className={`category-tag ${selectedTag === tag ? 'selected' : ''}`}
                    onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                  >
                    <TagOutlined /> {tag}
                  </Tag>
                ))}
              </Space>
            </div>

            {(selectedYear || selectedTag) && (
              <div className="active-filters">
                <h4>当前筛选：</h4>
                <div className="filter-chips">
                  {selectedYear && (
                    <Tag 
                      closable 
                      onClose={() => setSelectedYear(null)}
                      styles={{
                        closeIcon: { color: '#8b4513' }
                      }}
                    >
                      {selectedYear}年
                    </Tag>
                  )}
                  {selectedTag && (
                    <Tag 
                      closable 
                      onClose={() => setSelectedTag(null)}
                      styles={{
                        closeIcon: { color: '#8b4513' }
                      }}
                    >
                      {selectedTag}
                    </Tag>
                  )}
                  <span 
                    className="clear-all" 
                    onClick={clearFilters}
                  >
                    清除所有
                  </span>
                </div>
              </div>
            )}
          </Card>

          <Card 
            className="stats-card"
            styles={{
              body: { padding: '20px' }
            }}
          >
            <h3 className="card-title">
              <BookOutlined /> 归档统计
            </h3>
            <div className="stats-grid">
              <Statistic
                title="文物总数"
                value={posts.length}
                prefix={<BookOutlined />}
                className="stat-item"
                styles={{
                  title: { color: '#7d5d3b', fontSize: '14px' },
                  content: { color: '#5a3921', fontSize: '24px' }
                }}
              />
              <Statistic
                title="覆盖年份"
                value={years.length}
                prefix={<CalendarOutlined />}
                className="stat-item"
                styles={{
                  title: { color: '#7d5d3b', fontSize: '14px' },
                  content: { color: '#5a3921', fontSize: '24px' }
                }}
              />
              <Statistic
                title="标签数量"
                value={allTags.length}
                prefix={<TagOutlined />}
                className="stat-item"
                styles={{
                  title: { color: '#7d5d3b', fontSize: '14px' },
                  content: { color: '#5a3921', fontSize: '24px' }
                }}
              />
              <Statistic
                title="总字数"
                value={posts.reduce((sum, post) => sum + (post.content?.length || 0), 0)}
                className="stat-item"
                formatter={value => `${(value / 1000).toFixed(1)}k`}
                styles={{
                  title: { color: '#7d5d3b', fontSize: '14px' },
                  content: { color: '#5a3921', fontSize: '24px' }
                }}
              />
            </div>
          </Card>

          <Card 
            className="year-stats-card"
            styles={{
              body: { padding: '20px' }
            }}
          >
            <h3 className="card-title">年度摘要</h3>
            <div className="year-stats-list">
              {years.slice(0, 5).map(year => {
                const stats = getYearStats(year);
                return (
                  <div key={year} className="year-stat-item">
                    <div className="year-stat-header">
                      <span className="year-stat-year">{year}</span>
                      <span className="year-stat-count">{stats.postCount} 篇</span>
                    </div>
                    <div className="year-stat-meta">
                      <span>覆盖 {stats.monthCount} 个月</span>
                      <span>{stats.tagCount} 个标签</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* 主内容区 */}
        <div className="archive-main">
          {!selectedYear && !selectedTag ? (
            // 展示按年份分组的归档
            <div className="archive-list">
              {years.map(year => {
                const yearPosts = postsByYear[year];
                const stats = getYearStats(year);
                
                return (
                  <div key={year} className="year-section">
                    <div className="year-header">
                      <div className="year-info">
                        <div className="year-title">
                          <CalendarOutlined /> {year}年
                        </div>
                        <div className="year-subtitle">
                          {stats.monthCount}个月份 · {stats.tagCount}个分类 · {stats.postCount}件文物
                        </div>
                      </div>
                      <div 
                        className="year-toggle"
                        onClick={() => setSelectedYear(selectedYear === year ? null : year)}
                      >
                        {selectedYear === year ? '收起' : '展开'}
                      </div>
                    </div>

                    <div className="archive-items">
                      {yearPosts.map((post, index) => {
                        const month = new Date(post.date).getMonth() + 1;
                        return (
                          <ArchiveItem
                            key={post.id}
                            post={post}
                            year={year}
                            month={month}
                            index={index}
                            onViewDetails={(post) => {
                              // 处理详情查看逻辑
                              console.log('查看详情:', post);
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // 展示筛选结果
            <Card 
              className="filter-results-card"
              styles={{
                body: { padding: '25px' }
              }}
            >
              <div className="results-header">
                <h3>
                  筛选结果
                  <span className="results-count">
                    （共 {getFilteredPosts().length} 件文物）
                  </span>
                </h3>
                <span className="clear-filters" onClick={clearFilters}>
                  清除筛选条件
                </span>
              </div>

              {getFilteredPosts().length > 0 ? (
                <div className="filtered-archive-items">
                  {getFilteredPosts().map((post, index) => {
                    const year = post.date.split('-')[0];
                    const month = new Date(post.date).getMonth() + 1;
                    return (
                      <ArchiveItem
                        key={post.id}
                        post={post}
                        year={year}
                        month={month}
                        index={index}
                        onViewDetails={(post) => {
                          // 处理详情查看逻辑
                          console.log('查看详情:', post);
                        }}
                      />
                    );
                  })}
                </div>
              ) : (
                <Empty
                  description="没有找到符合条件的文物"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className="empty-state"
                >
                  <span className="empty-hint">
                    尝试调整筛选条件或 <span onClick={clearFilters}>清除所有筛选</span>
                  </span>
                </Empty>
              )}
            </Card>
          )}

          {/* 月份归档视图 */}
          <Card 
            className="month-archive-card"
            styles={{
              body: { padding: '25px' }
            }}
          >
            <h3 className="card-title">月度分布</h3>
            <div className="month-grid">
              {Object.entries(postsByMonth).slice(0, 12).map(([key, monthPosts]) => {
                const [year, month] = key.split('-');
                return (
                  <div 
                    key={key} 
                    className="month-item"
                    onClick={() => {
                      setSelectedYear(year);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    <div className="month-header">
                      <span className="month-name">{month}月</span>
                      <span className="month-count">{monthPosts.length}篇</span>
                    </div>
                    <div className="month-year">{year}年</div>
                    <div className="month-tags">
                      {Array.from(new Set(monthPosts.flatMap(p => p.tags)))
                        .slice(0, 2)
                        .map(tag => (
                          <Tag key={tag} size="small">{tag}</Tag>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Archive;